import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import React, { Suspense, lazy } from "react";
import Loader2 from "lucide-react/dist/esm/icons/loader-2"; // Lean import for initial load

// Auth provider (server-authoritative session/claims)
// import { AuthProvider } from "./app/providers/AuthProvider";
import AuthProvider from "./app/providers/AuthProvider";
import ProtectedRoute from "./app/routes/guards/ProtectedRoute";
import AdminRouteGuard from "./app/routes/guards/AdminRouteGuard";
import AutomationMonitoringPage from "./pages/admin/AutomationMonitoring/AutomationMonitoringPage";
import FeatureGuard from "./capabilities/FeatureGuard";

import { FK } from "./capabilities/featureKeys";

// Public Pages
import Login from "./pages/auth/Login";
import ForgotPassword from "./pages/auth/ForgotPassword";
import BusinessSignup from "./pages/auth/BusinessSignup";
import SignupForTrial from "./pages/auth/SignupForTrial";
import PendingApproval from "./pages/auth/PendingApproval";
import NoAccess from "./pages/NoAccess";
import PublicIntegrationsPage from "./pages/PublicIntegrations/PublicIntegrationsPage";

// Layout
import AppLayout from "./components/layout/AppLayout";
import WelcomeCenter from "./components/WelcomeCenter";
import AppHomeRoute from "./app/routes/AppHomeRoute";
import LegacyAiAgentRedirect from "./app/routes/LegacyAiAgentRedirect";
import LegacyInboxRedirect from "./app/routes/LegacyInboxRedirect";
import LegacyProfileRedirect from "./app/routes/LegacyProfileRedirect";
import NotFoundPage from "./pages/errors/NotFoundPage";

// Campaigns
import CTAManagement from "./pages/CTAManagement/CTAManagement";
import ImageCampaignDetailPage from "./pages/Campaigns/ImageCampaignDetailPage";
import ImageCampaignEditPage from "./pages/Campaigns/ImageCampaignEditPage";
import AssignContactsPage from "./pages/Campaigns/AssignContactsPage";
import RecipientsListPage from "./pages/Campaigns/components/RecipientsListPage";
import CampaignProgressPage from "./pages/Monitoring/CampaignProgressPage";
import FlowAnalyticsDashboard from "./pages/FlowAnalytics/FlowAnalyticsDashboard";
import CampaignLogReportPage from "./pages/CampaignReports/CampaignLogReportPage";
import CampaignBucketContactsPage from "./pages/CampaignReports/CampaignBucketContactsPage";
import CampaignReportsSelectorPage from "./pages/CampaignReports/CampaignReportsSelectorPage";
// Messaging
import SendTextMessagePage from "./pages/WhatsAppMessageEngine/SendContentFreeTextMessage";
import SendTemplateMessageInstantPage from "./pages/Messaging/SendTemplateMessageInstantPage";
import MessagingReportsComingSoon from "./pages/Messaging/MessagingReportsComingSoon";
import CatalogManagementPage from "./pages/Catalog/CatalogManagementPage";
import OrdersPage from "./pages/Orders/OrdersPage";
import SchedulingPage from "./pages/Scheduling/SchedulingPage";
import BookingWorkspacePage from "./pages/Scheduling/BookingWorkspacePage";
import SchedulingSettingsPage from "./pages/Scheduling/SchedulingSettingsPage";
import BookableServiceSetupPage from "./pages/Scheduling/BookableServiceSetupPage";

// Inbox & Automation
import AutoReplyBuilder from "./pages/AutoReplyBuilder/AutoReplyBuilder";
import AutoReplyLogs from "./pages/AutoReplyBuilder/AutoReplyLogs";
import AutoReplyTestConsole from "./pages/AutoReplyBuilder/AutoReplyTestConsole";
import AutomationRules from "./pages/Automation/AutomationRules";
import WhatsAppFlowsPage from "./pages/WhatsAppFlows/WhatsAppFlowsPage";
import CTAFlowVisualBuilder from "./pages/CTAFlowVisualBuilder/CTAFlowVisualBuilder";
import CTAFlowManager from "./pages/CTAFlowVisualBuilder/CTAFlowManager";
import AiAgentSettingsPage from "./pages/AiAgent/AiAgentSettingsPage";
import AiAgentUsagePage from "./pages/AiAgent/AiAgentUsagePage";
import AiKnowledgePage from "./pages/AiAgent/AiKnowledgePage";
import AiMediaAssetsPage from "./pages/AiAgent/AiMediaAssetsPage";
import AiTestConsolePage from "./pages/AiAgent/AiTestConsolePage";
import AiLeadQualificationConfigPage from "./pages/AiAgent/AiLeadQualificationConfigPage";
import AiLeadQualificationDebugPage from "./pages/AiAgent/AiLeadQualificationDebugPage";

// Admin Tools / Access Control
import UserPermissionOverrides from "./pages/admin/FeatureAccess/UserPermissionOverrides";
import ProductGuidanceAdminPage from "./pages/admin/ProductGuidance/ProductGuidanceAdminPage";
import PermissionsPage from "./pages/admin/AccessControl/PermissionsPage";
import PermissionCatalog from "./pages/admin/PermissionCatalog/PermissionCatalog";
import SignupOtpConfig from "./pages/admin/SignupOtpConfig";

// Tracking / Webhooks
import FailedWebhookLogs from "./pages/Tracking/FailedWebhookLogs";

// Template Builder
import LibraryBrowsePage from "./pages/TemplateBuilder/LibraryBrowsePage";
import DraftEditorPage from "./pages/TemplateBuilder/DraftEditorPage";
import ApprovedTemplatesPage from "./pages/TemplateBuilder/ApprovedTemplatesPage";
import TemplateBuilderLayout from "./pages/TemplateBuilder/TemplateBuilderLayout";
import DraftsListPage from "./pages/TemplateBuilder/DraftsListPage";

// Payment / Billing
import BillingPage from "./pages/Payment/BillingPage";
import WalletPage from "./pages/Payment/WalletPage";
import UsageChargesPage from "./pages/Pricing/Billing/UsageChargesPage";
import BusinessRatesPage from "./pages/Pricing/BusinessPricing/BusinessRatesPage";
import PartnerPricingPage from "./pages/Pricing/PartnerPricing/PartnerPricingPage";
import ResellerPricingPage from "./pages/Pricing/ResellerPricing/ResellerPricingPage";
import MetaRateCardsPage from "./pages/Pricing/MetaRateCards/MetaRateCardsPage";
import CreateBusinessPage from "./pages/Businesses/CreateBusinessPage";
import CreatePartnerPage from "./pages/Partners/CreatePartnerPage";
import CreateResellerPage from "./pages/Resellers/CreateResellerPage";

import Checkout from "./pages/Payment/CheckoutPage";
import PaymentStatusPage from "./pages/Payment/PaymentStatusPage";
import PaymentDebugPage from "./pages/Payment/PaymentDebugPage";

// WhatsApp / Meta Settings
import WhatsAppSettings from "./pages/WhatsAppSettings/WhatsAppSettings";
import MetaAccountManagement from "./pages/MetaAccount/MetaAccountManagement";
import EsuProcessingPage from "./pages/ESU/EsuProcessingPage";

// Account Insights (Admin analytics for accounts)
import AccountDashboard from "./pages/AccountInsights/AccountDashboard";
import AccountFunnels from "./pages/AccountInsights/AccountFunnels";
import AccountAlerts from "./pages/AccountInsights/AccountAlerts";
import AccountsMasterReport from "./pages/AccountInsights/AccountReports/AccountsMasterReport";
import LifecycleStageReport from "./pages/AccountInsights/AccountReports/LifecycleStageReport";
import TrialPerformanceReport from "./pages/AccountInsights/AccountReports/TrialPerformanceReport";
import RiskRecoveryReport from "./pages/AccountInsights/AccountReports/RiskRecoveryReport";
import ReportsIndex from "./pages/AccountInsights/AccountReports/ReportsIndex";

// Misc
import ProfileCompletion from "./pages/Businesses/ProfileCompletion";
import UpgradePlanPage from "./pages/Plans/UpgradePlanPage";
import PreviewTest from "./pages/PreviewTest";
import Forbidden403 from "./pages/errors/Forbidden403";
import EsuDebugPage from "./pages/DevTools/EsuDebugPage";
import MessageLogsReport from "./pages/reports/MessageLogsReport";

// Global upgrade modal & helpers
import UpgradeModal from "./components/UpgradeModal";
import AccessDeniedModal from "./components/AccessDeniedModal";
import { EntitlementsProvider } from "./app/providers/EntitlementsProvider";
import {
  TEMPLATE_BUILDER_ACCESS_PERMS,
  WORKSPACE_PERMS,
} from "./capabilities/workspacePerms";
import AccessDebugger from "./dev/AccessDebugger";
import MyAccountWorkspace from "./pages/Workspaces/MyAccountWorkspace";
import WelcomePage from "./pages/WelcomePages/WelcomePage";

// Audit / Logs
import FlowExecutionExplorer from "./pages/Auditing/FlowExecutionsExplorer";
import DeveloperNotesPage from "./pages/DeveloperNotes/DeveloperNotesPage";

// import { Tag } from "lucide-react";
import TeamManagementPage, { LegacyRolePermissionsRedirect } from "./pages/TeamStaff/TeamManagementPage";
import ChangePasswordPage from "./pages/Settings/Password/ChangePasswordPage";
import ContactConsentDebugPage from "./pages/Settings/ContactConsentDebugPage";
import WebsiteWidgetSettingsPage from "./pages/Settings/WebsiteWidgetSettingsPage";
import OrderCustomerUpdatesPage from "./pages/Settings/OrderCustomerUpdatesPage";
import OrderPoliciesPage from "./pages/Settings/OrderPoliciesPage";
import OrderPaymentsPage from "./pages/Settings/OrderPaymentsPage";
import IntegrationsPage from "./pages/Integrations/IntegrationsPage";

const isDev = process.env.NODE_ENV === "development";
const isNonProduction = process.env.NODE_ENV !== "production";

const LAZY_RELOAD_PREFIX = "lazy-reload:";

function lazyWithRetry(importer, key) {
  return lazy(async () => {
    const storageKey = `${LAZY_RELOAD_PREFIX}${key}`;
    const canUseSessionStorage =
      typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";
    const hasReloaded = canUseSessionStorage
      ? window.sessionStorage.getItem(storageKey) === "true"
      : false;

    try {
      const module = await importer();
      if (canUseSessionStorage) {
        window.sessionStorage.removeItem(storageKey);
      }
      return module;
    } catch (error) {
      const errorName = String(error?.name || "");
      const errorMessage = String(error?.message || "");
      const isChunkLoadError =
        errorName === "ChunkLoadError" ||
        /Loading chunk [\w-]+ failed/i.test(errorMessage) ||
        /Failed to fetch dynamically imported module/i.test(errorMessage);

      if (isChunkLoadError && canUseSessionStorage && !hasReloaded) {
        window.sessionStorage.setItem(storageKey, "true");
        window.location.reload();
        return new Promise(() => {});
      }

      if (canUseSessionStorage) {
        window.sessionStorage.removeItem(storageKey);
      }
      throw error;
    }
  });
}

// --- Lazy loaded components ---
const CrmWorkspacePage = lazyWithRetry(() => import("./pages/Workspaces/CrmWorkspacePage"), "CrmWorkspacePage");
const CampaignWorkspacePage = lazyWithRetry(() => import("./pages/Workspaces/CampaignWorkspacePage"), "CampaignWorkspacePage");
const AdminWorkspacePage = lazyWithRetry(() => import("./pages/Workspaces/AdminWorkspacePage"), "AdminWorkspacePage");
const MessagingWorkspacePage = lazyWithRetry(() => import("./pages/Workspaces/MessagingWorkspacePage"), "MessagingWorkspacePage");
const AutomationWorkspace = lazyWithRetry(() => import("./pages/Workspaces/AutomationWorkspace"), "AutomationWorkspace");
const ScheduledMessagesPage = lazyWithRetry(() => import("./pages/Automation/ScheduledMessages/ScheduledMessagesPage"), "ScheduledMessagesPage");
const AiAgentWorkspace = lazyWithRetry(() => import("./pages/Workspaces/AiAgentWorkspace"), "AiAgentWorkspace");
const InboxWorkspace = lazyWithRetry(() => import("./pages/Workspaces/InboxWorkspace"), "InboxWorkspace");
const TemplateBuilderWorkspacePage = lazyWithRetry(() => import("./pages/Workspaces/TemplateBuilderWorkspacePage"), "TemplateBuilderWorkspacePage");
const PrioritySlaDashboardPage = lazyWithRetry(() => import("./pages/Dashboard/PrioritySlaDashboardPage"), "PrioritySlaDashboardPage");
const InboxAnalyticsPage = lazyWithRetry(() => import("./pages/InboxAnalytics/InboxAnalytics"), "InboxAnalyticsPage");
const LeadCommandCentrePage = lazyWithRetry(() => import("./pages/Dashboard/LeadCommandCentrePage"), "LeadCommandCentrePage");
const LeadFilterSettingsPage = lazyWithRetry(() => import("./pages/Dashboard/LeadFilterSettingsPage"), "LeadFilterSettingsPage");

const Contacts = lazyWithRetry(() => import("./pages/CRM/Contacts/Contacts"), "Contacts");
const LeadsWorkbenchPage = lazyWithRetry(() => import("./pages/CRM/LeadsWorkbench/LeadsWorkbenchPage"), "LeadsWorkbenchPage");
const MyWorkPage = lazyWithRetry(() => import("./pages/CRM/MyWork/MyWorkPage"), "MyWorkPage");
const AttributesPage = lazyWithRetry(() => import("./pages/CustomFields/AttributesPage"), "AttributesPage");
const FollowUpTemplates = lazyWithRetry(() => import("./pages/CRM/FollowUpTemplates/FollowUpTemplates"), "FollowUpTemplates");
const WhatsAppReminderTemplates = lazyWithRetry(() => import("./pages/CRM/WhatsAppReminderTemplates/WhatsAppReminderTemplates"), "WhatsAppReminderTemplates");
const NotesWrapper = lazyWithRetry(() => import("./pages/CRM/Notes/NotesWrapper"), "NotesWrapper");
const Tags = lazyWithRetry(() => import("./pages/CRM/Tags/Tags"), "Tags");
const Contact360 = lazyWithRetry(() => import("./pages/CRM/Contact360/Contact360"), "Contact360");
const LeadAutomationRulesPage = lazyWithRetry(() => import("./pages/CRM/LeadAutomationRules/LeadAutomationWorkspace"), "LeadAutomationRulesPage");
const NotificationRecipientsPage = lazyWithRetry(() => import("./pages/CRM/LeadAutomationRules/LeadAutomationWorkspace").then(module => ({ default: module.LegacyNotificationRecipientsRedirect })), "NotificationRecipientsPage");

const BusinessApprovals = lazyWithRetry(() => import("./pages/Businesses/BusinessApprovals"), "BusinessApprovals");

const CampaignBuilderPage = lazyWithRetry(() => import("./pages/Campaigns/CampaignBuilderPage"), "CampaignBuilderPage");
const CampaignSendLogs = lazyWithRetry(() => import("./pages/Campaigns/CampaignSendLogs"), "CampaignSendLogs");
const TemplateCampaignList = lazyWithRetry(() => import("./pages/Campaigns/TemplateCampaignList"), "TemplateCampaignList");

const ChatInbox = lazyWithRetry(() => import("./pages/ChatInbox/ChatInbox"), "ChatInbox");

const WebhookSettings = lazyWithRetry(() => import("./pages/Tracking/WebhookSettings"), "WebhookSettings");
const PlanManagementPage = lazyWithRetry(() => import("./pages/admin/FeatureAccess/PlanManagement"), "PlanManagementPage");
const PricingDashboardPage = lazyWithRetry(() => import("./pages/Pricing/AdminPricing/AdminPricing"), "PricingDashboardPage");
const AdminWalletCreditPage = lazyWithRetry(() => import("./pages/admin/WalletCredit/AdminWalletCreditPage"), "AdminWalletCreditPage");

// Helper for root suspense
const RootLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-slate-50/50">
    <Loader2 className="animate-spin text-emerald-600" size={32} />
  </div>
);

// --- Helper Components ---
function DashboardRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: "/app/welcomepage", search, hash }} replace />;
}

function App() {
  return (
    <AuthProvider>
      {/* Wrap entire app tree with EntitlementsProvider */}
      <EntitlementsProvider>
        <Suspense fallback={<RootLoader />}>
          <Routes>
          {/* ---------- Public Routes ---------- */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/signup" element={<BusinessSignup />} />
          <Route path="/signup-for-trial" element={<SignupForTrial />} />
          <Route path="/pending-approval" element={<PendingApproval />} />
          <Route path="/no-access" element={<NoAccess />} />
          <Route path="/integrations" element={<PublicIntegrationsPage />} />

          {/* ---------- Protected App Routes (Layout + Workspaces) ---------- */}
          <Route
            path="/app/*"
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            {/* Home / errors */}
            <Route index element={<AppHomeRoute />} />
            <Route path="403" element={<Forbidden403 />} />
            <Route path="profile-completion" element={<LegacyProfileRedirect />} />
            <Route path="payment/status" element={<PaymentStatusPage />} />
            <Route
              path="payment/debug"
              element={
                isNonProduction ? (
                  <PaymentDebugPage />
                ) : (
                  <AdminRouteGuard>
                    <PaymentDebugPage />
                  </AdminRouteGuard>
                )
              }
            />
            <Route path="billing/checkout" element={<Checkout />} />
            <Route path="upgrade" element={<UpgradePlanPage />} />

            <Route
              path="settings/billing"
              element={
                <FeatureGuard code={FK.SETTINGS_BILLING_VIEW}>
                  <BillingPage />
                </FeatureGuard>
              }
            />
            <Route
              path="settings/wallet"
              element={
                <FeatureGuard code={FK.SETTINGS_WALLET_VIEW}>
                  <WalletPage />
                </FeatureGuard>
              }
            />
            <Route
              path="billing/usage"
              element={
                <FeatureGuard code={FK.SETTINGS_BILLING_USAGE_VIEW}>
                  <UsageChargesPage />
                </FeatureGuard>
              }
            />
            <Route
              path="business/pricing"
              element={
                <FeatureGuard code={FK.SETTINGS_BUSINESS_PRICING_VIEW}>
                  <BusinessRatesPage />
                </FeatureGuard>
              }
            />
            <Route
              path="partner/pricing"
              element={
                <FeatureGuard code={FK.SETTINGS_PARTNER_PRICING_VIEW}>
                  <PartnerPricingPage />
                </FeatureGuard>
              }
            />
            <Route
              path="reseller/pricing"
              element={
                <FeatureGuard code={FK.SETTINGS_RESELLER_PRICING_VIEW}>
                  <ResellerPricingPage />
                </FeatureGuard>
              }
            />
            <Route path="preview-test" element={<PreviewTest />} />

            {/* ===== Core Workspaces ===== */}

            {/* Welcome + Dashboard */}
            <Route
              path="dashboard"
              element={<DashboardRedirect />}
            />
            <Route
              path="dashboard/priority-sla"
              element={
                <FeatureGuard code={FK.DASHBOARD_VIEW}>
                  <PrioritySlaDashboardPage />
                </FeatureGuard>
              }
            />
            <Route
              path="dashboard/inbox-analytics"
              element={
                <FeatureGuard code={FK.DASHBOARD_INBOX_ANALYTICS_VIEW}>
                  <InboxAnalyticsPage />
                </FeatureGuard>
              }
            />
            <Route
              path="dashboard/lead-command-centre"
              element={
                <FeatureGuard code={FK.DASHBOARD_LEAD_COMMAND_CENTRE_VIEW}>
                  <LeadCommandCentrePage />
                </FeatureGuard>
              }
            />
            <Route
              path="dashboard/lead-command-centre/filter-settings"
              element={
                <FeatureGuard code={FK.DASHBOARD_LEAD_COMMAND_CENTRE_VIEW}>
                  <LeadFilterSettingsPage />
                </FeatureGuard>
              }
            />
            <Route path="welcome" element={<WelcomeCenter />} />
            <Route path="welcomepage" element={<WelcomePage />} />
            <Route path="esu-processing" element={<EsuProcessingPage />} />

            {/* ----- CRM Workspace + child routes ----- */}
            <Route
              path="crm"
              element={
                <FeatureGuard codes={WORKSPACE_PERMS.crm}>
                  <CrmWorkspacePage />
                </FeatureGuard>
              }
            />
            <Route
              path="crm/contacts"
              element={
                <FeatureGuard code={FK.CRM_CONTACT_VIEW}>
                  <Contacts />
                </FeatureGuard>
              }
            />
            <Route
              path="crm/leads-workbench"
              element={
                <FeatureGuard code={FK.CRM_LEADS_WORKBENCH_VIEW}>
                  <LeadsWorkbenchPage />
                </FeatureGuard>
              }
            />
            <Route
              path="crm/my-work"
              element={
                <FeatureGuard code={FK.CRM_MY_WORK_VIEW}>
                  <MyWorkPage />
                </FeatureGuard>
              }
            />
            <Route
              path="crm/attributes"
              element={
                <FeatureGuard codes={FK.CRM_ATTRIBUTE_VIEW}>
                  <AttributesPage />
                </FeatureGuard>
              }
            />
            <Route
              path="crm/contacts/:id"
              element={
                <FeatureGuard code={FK.CRM_CONTACT_DETAIL_VIEW}>
                  <Contact360 />
                </FeatureGuard>
              }
            />
            <Route
              path="crm/tags"
              element={
                <FeatureGuard code={FK.CRM_TAGS_VIEW}>
                  <Tags />
                </FeatureGuard>
              }
            />
            <Route
              path="crm/follow-up-templates"
              element={
                <FeatureGuard code={FK.CRM_FOLLOW_UP_PRESETS_VIEW}>
                  <FollowUpTemplates />
                </FeatureGuard>
              }
            />
            <Route
              path="crm/whatsapp-reminder-templates"
              element={
                <FeatureGuard code={FK.CRM_MESSAGE_PRESETS_VIEW}>
                  <WhatsAppReminderTemplates />
                </FeatureGuard>
              }
            />
            <Route
              path="crm/lead-automation-rules"
              element={
                <FeatureGuard codes={[FK.CRM_LEAD_AUTOMATION_RULES_VIEW, FK.CRM_NOTIFICATION_RECIPIENTS_VIEW]}>
                  <LeadAutomationRulesPage />
                </FeatureGuard>
              }
            />
            <Route
              path="crm/notification-recipients"
              element={
                <FeatureGuard code={FK.CRM_NOTIFICATION_RECIPIENTS_VIEW}>
                  <NotificationRecipientsPage />
                </FeatureGuard>
              }
            />


            <Route
              path="crm/contacts/:contactId/notes"
              element={
                <FeatureGuard code={FK.CRM_CONTACT_NOTES_VIEW}>
                  <NotesWrapper />
                </FeatureGuard>
              }
            />


            {/* ----- Messaging Workspace + child routes ----- */}
            {/* ----- Messaging Workspace + child routes ----- */}
            <Route
              path="messaging"
              element={
                <FeatureGuard codes={WORKSPACE_PERMS.messaging}>
                  {/* fallback={<Navigate to="/no-access" replace />}
                > */}
                  <MessagingWorkspacePage />
                </FeatureGuard>
              }
            />
            <Route
              path="messaging/send-direct-text"
              element={
                <FeatureGuard code={FK.MESSAGING_SEND_TEXT}>
                  <SendTextMessagePage />
                </FeatureGuard>
              }
            />
            <Route
              path="messaging/send-template-message"
              element={
                <FeatureGuard code={FK.MESSAGING_SEND_TEMPLATE}>
                  <SendTemplateMessageInstantPage />
                </FeatureGuard>
              }
            />
            <Route
              path="messaging/reports"
              element={
                <FeatureGuard code={FK.MESSAGING_REPORT_VIEW}>
                  <MessagingReportsComingSoon />
                </FeatureGuard>
              }
            />

            {/* ----- Campaigns Workspace + child routes ----- */}
            <Route
              path="campaigns"
              element={
                <FeatureGuard codes={WORKSPACE_PERMS.campaigns}>
                  <CampaignWorkspacePage />
                </FeatureGuard>
              }
            />

            {/* Campaigns subroutes */}
            <Route
              path="campaigns/cta-management"
              element={
                <FeatureGuard code={FK.CAMPAIGN_CTA_MANAGEMENT}>
                  <CTAManagement />
                </FeatureGuard>
              }
            />
            <Route
              path="campaigns/image-campaigns/:id"
              element={
                <FeatureGuard code={FK.CAMPAIGN_IMAGE_DETAIL_VIEW}>
                  <ImageCampaignDetailPage />
                </FeatureGuard>
              }
            />
            <Route
              path="campaigns/image-campaigns/:id/edit"
              element={
                <FeatureGuard code={FK.CAMPAIGN_IMAGE_EDIT}>
                  <ImageCampaignEditPage />
                </FeatureGuard>
              }
            />
            <Route
              path="campaigns/image-campaigns/assign-contacts/:id"
              element={
                <FeatureGuard code={FK.CAMPAIGN_ASSIGN_CONTACTS}>
                  <AssignContactsPage />
                </FeatureGuard>
              }
            />
            <Route
              path="campaigns/image-campaigns/assigned-contacts/:id"
              element={
                <FeatureGuard code={FK.CAMPAIGN_ASSIGNED_CONTACTS_VIEW}>
                  <RecipientsListPage />
                </FeatureGuard>
              }
            />
            <Route
              path="campaigns/template-campaign-builder"
              element={
                <FeatureGuard code={FK.CAMPAIGN_BUILDER}>
                  <CampaignBuilderPage />
                </FeatureGuard>
              }
            />
            <Route
              path="campaigns/template-campaign-builder/:campaignId/edit"
              element={
                <FeatureGuard code={FK.CAMPAIGN_BUILDER}>
                  <CampaignBuilderPage />
                </FeatureGuard>
              }
            />
            <Route
              path="campaigns/edit/:campaignId"
              element={
                <FeatureGuard code={FK.CAMPAIGN_BUILDER}>
                  <CampaignBuilderPage />
                </FeatureGuard>
              }
            />
            <Route
              path="campaigns/template-campaigns-list"
              element={
                <FeatureGuard code={FK.CAMPAIGN_LIST_VIEW}>
                  <TemplateCampaignList />
                </FeatureGuard>
              }
            />
            <Route
              path="campaigns/logs/:campaignId"
              element={
                <FeatureGuard code={FK.CAMPAIGN_DETAIL_LOGS_VIEW}>
                  <CampaignSendLogs />
                </FeatureGuard>
              }
            />
            <Route
              path="campaigns/messagelogs"
              element={
                <FeatureGuard code={FK.CAMPAIGN_MESSAGE_LOGS_VIEW}>
                  <MessageLogsReport />
                </FeatureGuard>
              }
            />
            <Route
              path="campaigns/reports"
              element={
                <FeatureGuard code={FK.CAMPAIGN_REPORT_LOGS_VIEW}>
                  <CampaignReportsSelectorPage />
                </FeatureGuard>
              }
            />
            <Route
              path="campaigns/:campaignId/progress"
              element={
                <FeatureGuard code={FK.CAMPAIGN_PROGRESS_VIEW}>
                  <CampaignProgressPage />
                </FeatureGuard>
              }
            />
            <Route
              path="campaigns/:campaignId/reports/logs"
              element={
                <FeatureGuard code={FK.CAMPAIGN_REPORT_LOGS_VIEW}>
                  <CampaignLogReportPage />
                </FeatureGuard>
              }
            />
            <Route
              path="campaigns/:campaignId/reports/:bucket"
              element={
                <FeatureGuard code={FK.CAMPAIGN_REPORT_BUCKET_VIEW}>
                  <CampaignBucketContactsPage />
                </FeatureGuard>
              }
            />

            {/* ----- Inbox Workspace + child routes ----- */}
            <Route
              path="inbox"
              element={
                <FeatureGuard codes={WORKSPACE_PERMS.inbox}>
                  <InboxWorkspace />
                </FeatureGuard>
              }
            />
            <Route
              path="inbox/livechat"
              element={<LegacyInboxRedirect />}
            />
            <Route
              path="inbox/chatinbox"
              element={
                <FeatureGuard code={FK.INBOX_CHAT_VIEW}>
                  <ChatInbox />
                </FeatureGuard>
              }
            />

            <Route
              path="catalog"
              element={
                <FeatureGuard code={FK.CATALOG_VIEW}>
                  <CatalogManagementPage />
                </FeatureGuard>
              }
            />
            <Route
              path="orders"
              element={
                <FeatureGuard code={FK.COMMERCE_TRANSACTION_VIEW}>
                  <OrdersPage />
                </FeatureGuard>
              }
            />
            <Route
              path="scheduling"
              element={
                <FeatureGuard code={FK.SCHEDULING_VIEW}>
                  <SchedulingPage />
                </FeatureGuard>
              }
            />

            <Route path="scheduling/bookings/:bookingId" element={
              <FeatureGuard code={FK.SCHEDULING_VIEW}>
                <FeatureGuard code={FK.COMMERCE_TRANSACTION_VIEW}>
                  <FeatureGuard code={FK.COMMERCE_PAYMENT_VIEW}><BookingWorkspacePage /></FeatureGuard>
                </FeatureGuard>
              </FeatureGuard>
            } />

            {/* ----- Automation Workspace + flows ----- */}
            <Route
              path="automation"
              element={
                <FeatureGuard codes={WORKSPACE_PERMS.automation}>
                  <AutomationWorkspace />
                </FeatureGuard>
              }
            />
            <Route
              path="automation/auto-reply-builder"
              element={
                <FeatureGuard code={FK.AUTOMATION_CREATE_BOT}>
                  <AutoReplyBuilder />
                </FeatureGuard>
              }
            />
            <Route
              path="automation/autoreply-logs"
              element={
                <FeatureGuard code={FK.AUTOMATION_AUTOREPLY_LOGS_VIEW}>
                  <AutoReplyLogs />
                </FeatureGuard>
              }
            />
            <Route
              path="automation/autoreply-test-console"
              element={
                <FeatureGuard code={FK.AUTOMATION_AUTOREPLY_TEST_CONSOLE}>
                  <AutoReplyTestConsole />
                </FeatureGuard>
              }
            />
            <Route
              path="automation/rules"
              element={
                <FeatureGuard code={FK.AUTOMATION_RULES_VIEW}>
                  <AutomationRules />
                </FeatureGuard>
              }
            />
            <Route
              path="automation/scheduled-messages"
              element={
                <FeatureGuard featureKey={FK.AUTOMATION_SCHEDULED_MESSAGES_VIEW}>
                  <ScheduledMessagesPage />
                </FeatureGuard>
              }
            />
            <Route
              path="automation/whatsapp-forms"
              element={
                <FeatureGuard code={FK.WHATSAPP_FLOW_VIEW}>
                  <WhatsAppFlowsPage />
                </FeatureGuard>
              }
            />

            {/* ----- AI Agent Workspace + canonical routes ----- */}
            <Route
              path="ai-agent"
              element={
                <FeatureGuard codes={WORKSPACE_PERMS.aiAgent}>
                  <AiAgentWorkspace />
                </FeatureGuard>
              }
            />
            <Route
              path="ai-agent/settings"
              element={
                <FeatureGuard code={FK.AI_AGENT_SETTINGS_VIEW}>
                  <AiAgentSettingsPage />
                </FeatureGuard>
              }
            />
            <Route
              path="ai-agent/usage"
              element={
                <FeatureGuard code={FK.AI_AGENT_USAGE_VIEW}>
                  <AiAgentUsagePage />
                </FeatureGuard>
              }
            />
            <Route
              path="ai-agent/lead-qualification"
              element={
                <FeatureGuard code={FK.AI_AUTO_REPLY_MANAGE}>
                  <AiLeadQualificationConfigPage />
                </FeatureGuard>
              }
            />
            <Route
              path="ai-agent/lead-qualification/debug"
              element={
                <FeatureGuard code={FK.AI_AUTO_REPLY_MANAGE}>
                  <AiLeadQualificationDebugPage />
                </FeatureGuard>
              }
            />
            <Route
              path="ai-agent/knowledge"
              element={
                <FeatureGuard code={FK.AI_KNOWLEDGE_VIEW}>
                  <AiKnowledgePage />
                </FeatureGuard>
              }
            />
            <Route
              path="ai-agent/media-assets"
              element={
                <FeatureGuard code={FK.AI_MEDIA_ASSETS_VIEW}>
                  <AiMediaAssetsPage />
                </FeatureGuard>
              }
            />
            <Route
              path="ai-agent/test"
              element={
                <FeatureGuard
                  codes={[FK.AI_AGENT_ENABLED, FK.AI_TEST_CONSOLE_USE]}
                  requireAllPermissions
                >
                  <AiTestConsolePage />
                </FeatureGuard>
              }
            />

            {/* Legacy redirects retained for deep-link compatibility. */}
            <Route
              path="automation/ai-agent/settings"
              element={
                <FeatureGuard code={FK.AI_AGENT_SETTINGS_VIEW}>
                  <LegacyAiAgentRedirect to="/app/ai-agent/settings" />
                </FeatureGuard>
              }
            />
            <Route
              path="automation/ai-agent/usage"
              element={
                <FeatureGuard code={FK.AI_AGENT_USAGE_VIEW}>
                  <LegacyAiAgentRedirect to="/app/ai-agent/usage" />
                </FeatureGuard>
              }
            />
            <Route
              path="automation/ai-agent/lead-qualification"
              element={
                <FeatureGuard code={FK.AI_AUTO_REPLY_MANAGE}>
                  <LegacyAiAgentRedirect to="/app/ai-agent/lead-qualification" />
                </FeatureGuard>
              }
            />
            <Route
              path="automation/ai-agent/lead-qualification/debug"
              element={
                <FeatureGuard code={FK.AI_AUTO_REPLY_MANAGE}>
                  <LegacyAiAgentRedirect to="/app/ai-agent/lead-qualification/debug" />
                </FeatureGuard>
              }
            />
            <Route
              path="automation/ai-agent/knowledge"
              element={
                <FeatureGuard code={FK.AI_KNOWLEDGE_VIEW}>
                  <LegacyAiAgentRedirect to="/app/ai-agent/knowledge" />
                </FeatureGuard>
              }
            />
            <Route
              path="automation/ai-agent/media-assets"
              element={
                <FeatureGuard code={FK.AI_MEDIA_ASSETS_VIEW}>
                  <LegacyAiAgentRedirect to="/app/ai-agent/media-assets" />
                </FeatureGuard>
              }
            />
            <Route
              path="automation/ai-agent/test"
              element={
                <FeatureGuard
                  codes={[FK.AI_AGENT_ENABLED, FK.AI_TEST_CONSOLE_USE]}
                  requireAllPermissions
                >
                  <LegacyAiAgentRedirect to="/app/ai-agent/test" />
                </FeatureGuard>
              }
            />
            {/* Flow Builder direct routes */}
            <Route
              path="cta-flow/visual-builder"
              element={
                <FeatureGuard code={FK.AUTOMATION_CREATE_TEMPLATE_FLOW}>
                  <CTAFlowVisualBuilder />
                </FeatureGuard>
              }
            />

            <Route
              path="cta-flow/flow-manager"
              element={
                <FeatureGuard code={FK.AUTOMATION_VIEW_FLOW_MANAGE}>
                  <CTAFlowManager />
                </FeatureGuard>
              }
            />
            <Route
              path="campaigns/FlowAnalyticsDashboard"
              element={
                <FeatureGuard code={FK.AUTOMATION_VIEW_FLOW_ANALYTICS}>
                  <FlowAnalyticsDashboard />
                </FeatureGuard>
              }
            />

            {/* ----- Template Builder Workspace + routes (under /app) ----- */}

            <Route
              path="templatebuilder"
              element={
                <FeatureGuard codes={TEMPLATE_BUILDER_ACCESS_PERMS}>
                  <TemplateBuilderWorkspacePage />
                </FeatureGuard>
              }
            />

            {/* Unified Template Builder Layout */}
            <Route path="template-builder" element={<TemplateBuilderLayout />}>
              <Route index element={<Navigate to="library" replace />} />

              <Route
                path="library"
                element={
                  <FeatureGuard code={FK.TEMPLATE_BUILDER_LIBRARY_BROWSE}>
                    <LibraryBrowsePage />
                  </FeatureGuard>
                }
              />
              <Route
                path="drafts"
                element={
                  <FeatureGuard
                    codes={[
                      FK.TEMPLATE_BUILDER_CREATE_DRAFT,
                      FK.TEMPLATE_BUILDER_DRAFT_EDITOR_VIEW,
                    ]}
                  >
                    <DraftsListPage />
                  </FeatureGuard>
                }
              />
              <Route
                path="pending"
                element={
                  <FeatureGuard
                    code={FK.TEMPLATE_BUILDER_PENDING_TEMPLATES_VIEW}
                  >
                    <ApprovedTemplatesPage forcedStatus="PENDING" />
                  </FeatureGuard>
                }
              />
              <Route
                path="rejected"
                element={
                  <FeatureGuard code={FK.TEMPLATE_BUILDER_APPROVED_TEMPLATES_VIEW}>
                    <ApprovedTemplatesPage forcedStatus="REJECTED" />
                  </FeatureGuard>
                }
              />
              <Route
                path="approved"
                element={
                  <FeatureGuard
                    code={FK.TEMPLATE_BUILDER_APPROVED_TEMPLATES_VIEW}
                  >
                    <ApprovedTemplatesPage />
                  </FeatureGuard>
                }
              />
            </Route>

            <Route
              path="template-builder/drafts/:draftId"
              element={
                <FeatureGuard
                  codes={[
                    FK.TEMPLATE_BUILDER_DRAFT_EDITOR_VIEW,
                    FK.TEMPLATE_BUILDER_CREATE_DRAFT,
                  ]}
                >
                  <DraftEditorPage />
                </FeatureGuard>
              }
            />
            {/* ----- Settings Workspace + child routes ----- */}
            <Route
              path="settings"
              element={
                <FeatureGuard codes={WORKSPACE_PERMS.settings}>
                  <MyAccountWorkspace />
                </FeatureGuard>
              }
            />
            <Route
              path="settings/whatsapp"
              element={
                <FeatureGuard code={FK.SETTINGS_WHATSAPP_SETTINGS_VIEW}>
                  <WhatsAppSettings />
                </FeatureGuard>
              }
            />
            <Route
              path="settings/integrations"
              element={
                <FeatureGuard code={FK.SETTINGS_INTEGRATIONS_VIEW}>
                  <IntegrationsPage />
                </FeatureGuard>
              }
            />
            <Route
              path="settings/website-widget"
              element={
                <FeatureGuard code={FK.SETTINGS_WEBSITE_WIDGET_VIEW}>
                  <WebsiteWidgetSettingsPage />
                </FeatureGuard>
              }
            />
            <Route
              path="settings/meta-account"
              element={
                <FeatureGuard code={FK.SETTINGS_META_ACCOUNT_MANAGEMENT}>
                  <MetaAccountManagement />
                </FeatureGuard>
              }
            />

            <Route
              path="settings/team-management"
              element={<TeamManagementPage />}
            />

            <Route
              path="settings/role-permission-mapping"
              element={
                <FeatureGuard code={FK.SETTINGS_ROLE_PERMISSIONS_MAPPING}>
                  <LegacyRolePermissionsRedirect />
                </FeatureGuard>
              }
            />

            {/* <Route
              path="settings/billing"
              element={
                <FeatureGuard featureKey={FK.SETTINGS_BILLING_VIEW}>
                  <BillingPage />
                </FeatureGuard>
              }
            /> */}

            <Route
              path="settings/checkout"
              element={
                <FeatureGuard code={FK.SETTINGS_CHECKOUT_VIEW}>
                  <Checkout />
                </FeatureGuard>
              }
            />
            <Route
              path="settings/profile-completion"
              element={
                <FeatureGuard code={FK.SETTINGS_PROFILE_UPDATE}>
                  <ProfileCompletion />
                </FeatureGuard>
              }
            />
            <Route
              path="settings/password"
              element={
                <FeatureGuard codes={FK.SETTINGS_PASSWORD_UPDATE}>
                  <ChangePasswordPage />
                </FeatureGuard>
              }
            />
            <Route
              path="settings/contact-consent-debug"
              element={
                <AdminRouteGuard>
                  <FeatureGuard codes={FK.SETTINGS_CONTACT_CONSENT_DEBUG}>
                    <ContactConsentDebugPage />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />
            <Route
              path="integrations"
              element={
                <Navigate to="/app/settings/integrations" replace />
              }
            />
            {/* ----- Admin Workspace + tools ----- */}
            {/* ----- Admin Workspace + tools ----- */}
            <Route
              path="admin"
              element={
                <AdminRouteGuard>
                  <FeatureGuard codes={WORKSPACE_PERMS.superadmin}>
                    <AdminWorkspacePage />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />
            <Route
              path="admin/approvals"
              element={
                <FeatureGuard code={FK.SUPER_ADMIN_NEW_BUSINESS_APPROVAL}>
                  <BusinessApprovals />
                </FeatureGuard>
              }
            />
            <Route
              path="scheduling/setup"
              element={
                <FeatureGuard codes={[FK.SCHEDULING_VIEW, FK.CATALOG_VIEW]} requireAllPermissions>
                  <BookableServiceSetupPage />
                </FeatureGuard>
              }
            />
            <Route
              path="settings/scheduling"
              element={
                <FeatureGuard code={FK.SCHEDULING_VIEW}>
                  <SchedulingSettingsPage />
                </FeatureGuard>
              }
            />
            <Route
              path="settings/order-policies"
              element={
                <FeatureGuard codes={[
                  FK.SETTINGS_PROFILE_UPDATE,
                  FK.COMMERCE_TRANSACTION_VIEW,
                ]}>
                  <OrderPoliciesPage />
                </FeatureGuard>
              }
            />
            <Route
              path="settings/order-payments"
              element={
                <FeatureGuard code={FK.COMMERCE_PAYMENT_VIEW}>
                  <OrderPaymentsPage />
                </FeatureGuard>
              }
            />
            <Route
              path="settings/order-customer-updates"
              element={
                <FeatureGuard code={FK.SETTINGS_PROFILE_UPDATE}>
                  <OrderCustomerUpdatesPage />
                </FeatureGuard>
              }
            />
            <Route
              path="admin/businesses/create"
              element={
                <AdminRouteGuard>
                  <FeatureGuard code={FK.PLATFORM_BUSINESS_CREATE_VIEW}>
                    <CreateBusinessPage />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />
            <Route
              path="admin/partners/create"
              element={
                <AdminRouteGuard>
                  <FeatureGuard code={FK.PLATFORM_PARTNER_CREATE_VIEW}>
                    <CreatePartnerPage />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />
            <Route
              path="admin/resellers/create"
              element={
                <AdminRouteGuard>
                  <FeatureGuard code={FK.PLATFORM_RESELLER_CREATE_VIEW}>
                    <CreateResellerPage />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />
            {/* NEW: User Permission Overrides (Admin-only) */}
            <Route
              path="admin/user-permissions"
              element={
                <AdminRouteGuard>
                  <FeatureGuard code={FK.SUPER_ADMIN_USER_MANAGEMENT_VIEW}>
                    <UserPermissionOverrides />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />
            <Route
              path="admin/plan-management"
              element={
                <AdminRouteGuard>
                  <FeatureGuard code={FK.SUPER_ADMIN_PLAN_MANAGER_VIEW}>
                    <PlanManagementPage />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />
            <Route
              path="admin/product-guidance"
              element={
                <AdminRouteGuard>
                  <FeatureGuard code={FK.SUPER_ADMIN_PRODUCT_GUIDANCE_MANAGE}>
                    <ProductGuidanceAdminPage />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />
            <Route
              path="admin/pricing"
              element={
                <AdminRouteGuard>
                  <FeatureGuard code={FK.SUPER_ADMIN_PRICING_MANAGEMENT_VIEW}>
                    <PricingDashboardPage />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />
            <Route
              path="admin/meta-rate-cards"
              element={
                <AdminRouteGuard>
                  <FeatureGuard code={FK.SUPER_ADMIN_META_RATE_CARD_MANAGEMENT_VIEW}>
                    <MetaRateCardsPage />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />
            <Route
              path="admin/wallet-credit"
              element={
                <AdminRouteGuard>
                  <FeatureGuard
                    codes={[
                      FK.ADMIN_WALLET_CREDIT,
                      FK.ADMIN_WALLET_DEBIT,
                      FK.ADMIN_BUSINESS_BILLING_MODE_MANAGE,
                    ]}
                  >
                    <AdminWalletCreditPage />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />

            {/* NEW: Permissions admin UI */}
            <Route
              path="admin/permissions"
              element={
                <AdminRouteGuard>
                  <FeatureGuard code={FK.SUPER_ADMIN_PLAN_PERMISSIONS_LIST}>
                    <PermissionsPage />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />
            {/* (Unused here but imported) */}
            <Route
              path="admin/permission-catalog"
              element={
                <AdminRouteGuard>
                  <FeatureGuard code={FK.SUPER_ADMIN_PERMISSION_CATALOG_VIEW}>
                    <PermissionCatalog />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />
            <Route
              path="admin/signup-otp-config"
              element={
                <AdminRouteGuard>
                  <FeatureGuard code={FK.SUPER_ADMIN_SIGNUP_OTP_CONFIG_VIEW}>
                    <SignupOtpConfig />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />

            {/* ----- Account Insights (Admin-only cross-account intelligence) ----- */}
            <Route
              path="admin/account-insights"
              element={
                <AdminRouteGuard>
                  <FeatureGuard code={FK.SUPER_ADMIN_SIGNUP_REPORT_VIEW}>
                    <AccountDashboard />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />
            <Route
              path="admin/account-insights/funnels"
              element={
                <AdminRouteGuard>
                  <FeatureGuard code={FK.SUPER_ADMIN_ACCOUNT_INSIGHTS_FUNNELS_VIEW}>
                    <AccountFunnels />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />
            <Route
              path="admin/account-insights/alerts"
              element={
                <AdminRouteGuard>
                  <FeatureGuard code={FK.SUPER_ADMIN_ACCOUNT_INSIGHTS_ALERTS_VIEW}>
                    <AccountAlerts />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />
            <Route
              path="admin/esu-debug"
              element={
                <AdminRouteGuard>
                  <FeatureGuard code={FK.SUPER_ADMIN_ESU_DEBUG}>
                    <EsuDebugPage />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />
            {/* Account Insights - Detailed Reports */}
            <Route
              path="admin/account-insights/account-reports"
              element={
                <AdminRouteGuard>
                  <FeatureGuard code={FK.SUPER_ADMIN_BUSINESS_OVERVIEW}>
                    <ReportsIndex />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />
            <Route
              path="admin/account-insights/account-reports/accounts-master"
              element={
                <AdminRouteGuard>
                  <FeatureGuard code={FK.SUPER_ADMIN_ACCOUNT_REPORTS_ACCOUNTS_MASTER_VIEW}>
                    <AccountsMasterReport />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />
            <Route
              path="admin/account-insights/account-reports/lifecycle"
              element={
                <AdminRouteGuard>
                  <FeatureGuard code={FK.SUPER_ADMIN_ACCOUNT_REPORTS_LIFECYCLE_VIEW}>
                    <LifecycleStageReport />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />
            <Route
              path="admin/account-insights/account-reports/trials"
              element={
                <AdminRouteGuard>
                  <FeatureGuard code={FK.SUPER_ADMIN_ACCOUNT_REPORTS_TRIALS_VIEW}>
                    <TrialPerformanceReport />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />
            <Route
              path="admin/account-insights/account-reports/risk"
              element={
                <AdminRouteGuard>
                  <FeatureGuard code={FK.SUPER_ADMIN_ACCOUNT_REPORTS_RISK_VIEW}>
                    <RiskRecoveryReport />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />
            <Route
              path="admin/automation-monitoring"
              element={
                <AdminRouteGuard superAdminOnly>
                  <AutomationMonitoringPage />
                </AdminRouteGuard>
              }
            />
            <Route
              path="admin/webhooks/monitor"
              element={
                <AdminRouteGuard>
                  <FeatureGuard code={FK.SUPER_ADMIN_WEBHOOK_MONITOR}>
                    <WebhookSettings />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />
            <Route
              path="admin/webhooks/failedlog"
              element={
                <AdminRouteGuard>
                  <FeatureGuard code={FK.SUPER_ADMIN_WEBHOOK_FAILEDLOG_VIEW}>
                    <FailedWebhookLogs />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />

            <Route
              path="admin/audit/execution-explorer"
              element={
                <AdminRouteGuard>
                  <FeatureGuard
                    code={FK.SUPER_ADMIN_FLOW_EXECUTION_EXPLORER_VIEW}
                  >
                    <FlowExecutionExplorer />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />

            <Route
              path="admin/developer-notes"
              element={
                <AdminRouteGuard>
                  <FeatureGuard codes={FK.SUPER_ADMIN_DEVELOPER_NOTES_VIEW}>
                    <DeveloperNotesPage />
                  </FeatureGuard>
                </AdminRouteGuard>
              }
            />
            <Route path="*" element={<NotFoundPage authenticated />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>

        {/* Toasts */}
        <ToastContainer
          position="bottom-right"
          autoClose={2000}
          hideProgressBar={true}
          newestOnTop
          closeOnClick
          pauseOnHover={false}
          pauseOnFocusLoss={false}
          draggable
          closeButton={false}
          theme="light"
        />

        {/* Global upgrade modal for feature/quota denials */}
        <UpgradeModal />
        <AccessDeniedModal />
        {isDev && <AccessDebugger />}
        </Suspense>
      </EntitlementsProvider>
    </AuthProvider>
  );
}

export default App;



